import { Instagram, Mail, Phone } from "lucide-react";

export const Footer = () => {
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-black/5 bg-[#FDFCF8]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="mb-4">
              <img
                src="/logo.jpg"
                alt="Neurological Communication Partners"
                className="h-20 w-auto rounded-xl ring-1 ring-black/5"
              />
            </div>
            <p className="text-[#5C6661] text-sm leading-relaxed max-w-sm">
              Adult speech-language pathology (SLP) practice founded by Kathleen
              McGrath, M.S. CCC-SLP. Licensed in New Hampshire, Vermont and
              Ireland.
            </p>

            <p className="mt-6 text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-3">
              Serving
            </p>
            <ul className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
              <li>
                <a
                  href="/new-hampshire"
                  data-testid="footer-loc-nh"
                  className="link-underline text-[#2D332F]/80"
                >
                  New Hampshire
                </a>
              </li>
              <li>
                <a
                  href="/vermont"
                  data-testid="footer-loc-vt"
                  className="link-underline text-[#2D332F]/80"
                >
                  Vermont
                </a>
              </li>
              <li>
                <a
                  href="/ireland"
                  data-testid="footer-loc-ie"
                  className="link-underline text-[#2D332F]/80"
                >
                  Ireland
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-4">
              Practice
            </p>
            <ul className="space-y-2.5 text-sm text-[#2D332F]/80">
              <li>
                <a
                  href="#specialties"
                  className="link-underline"
                  data-testid="footer-link-specialties"
                >
                  Specialties
                </a>
              </li>
              <li>
                <a
                  href="#treatments"
                  className="link-underline"
                  data-testid="footer-link-treatments"
                >
                  Adult Treatments
                </a>
              </li>
              <li>
                <a
                  href="#telehealth"
                  className="link-underline"
                  data-testid="footer-link-telehealth"
                >
                  Telehealth
                </a>
              </li>
              <li>
                <a
                  href="#about"
                  className="link-underline"
                  data-testid="footer-link-about"
                >
                  About Kathleen
                </a>
              </li>
              <li>
                <a
                  href="/what-to-expect"
                  className="link-underline"
                  data-testid="footer-link-what-to-expect"
                >
                  What to expect
                </a>
              </li>
              <li>
                <a
                  href="/fees"
                  className="link-underline"
                  data-testid="footer-link-fees"
                >
                  Fees
                </a>
              </li>
              <li>
                <a
                  href="/for-physicians"
                  className="link-underline"
                  data-testid="footer-link-for-physicians"
                >
                  For physicians
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <p className="text-xs uppercase tracking-[0.2em] text-[#5C6661] mb-4">
              Connect
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href="https://instagram.com/Neurocompartners"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-instagram"
                  className="inline-flex items-center gap-2 text-[#2D332F]/80 hover:text-[#739072] transition-colors"
                >
                  <Instagram size={16} strokeWidth={1.5} />
                  @Neurocompartners
                </a>
              </li>
              <li>
                <a
                  href="mailto:neurocompartners@gmail.com"
                  data-testid="footer-email"
                  className="inline-flex items-center gap-2 text-[#2D332F]/80 hover:text-[#739072] transition-colors break-all"
                >
                  <Mail size={16} strokeWidth={1.5} />
                  neurocompartners@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="tel:+15187955154"
                  data-testid="footer-phone-us"
                  className="inline-flex items-center gap-2 text-[#2D332F]/80 hover:text-[#739072] transition-colors"
                >
                  <Phone size={16} strokeWidth={1.5} />
                  +1 (518) 795-5154 &nbsp;
                  <span className="text-[#5C6661] text-xs uppercase tracking-[0.18em]">
                    USA
                  </span>
                </a>
              </li>
              <li>
                <a
                  href="tel:+353873773620"
                  data-testid="footer-phone-ie"
                  className="inline-flex items-center gap-2 text-[#2D332F]/80 hover:text-[#739072] transition-colors"
                >
                  <Phone size={16} strokeWidth={1.5} />
                  +353 87 377 3620 &nbsp;
                  <span className="text-[#5C6661] text-xs uppercase tracking-[0.18em]">
                    Ireland
                  </span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="divider-line my-10" />

        <div
          data-testid="footer-certifications"
          className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10"
        >
          <div className="max-w-md">
            <p className="text-xs uppercase tracking-[0.22em] text-[#5C6661] mb-2">
              Certifications &amp; affiliations
            </p>
            <p className="text-sm text-[#2D332F]/80 leading-relaxed">
              Kathleen is a{" "}
              <strong className="text-[#2D332F]">
                Certified SPEAK OUT!® Provider
              </strong>{" "}
              with the Parkinson Voice Project — the evidence-based voice
              programme for adults living with Parkinson&rsquo;s disease.
            </p>
          </div>
          <a
            href="https://www.parkinsonvoiceproject.org/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-speakout-badge"
            className="inline-block rounded-2xl bg-white p-3 ring-1 ring-black/5 hover:ring-[#739072]/40 hover:-translate-y-0.5 transition-all duration-300"
            aria-label="Certified SPEAK OUT! Provider — Parkinson Voice Project"
          >
            <img
              src="/speakout-badge.jpg"
              alt="Certified SPEAK OUT! Provider through July 2026 — Parkinson Voice Project"
              loading="lazy"
              className="h-24 md:h-28 w-auto"
            />
          </a>
        </div>

        <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between text-xs text-[#5C6661]">
          <p>
            © {new Date().getFullYear()} Neurological Communication Partners. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <p>
              Licensed in New Hampshire, Vermont (ASHA CCC-SLP) &amp; Ireland
              (CORU). Therapy content for educational purposes only.
            </p>
            <a
              href="/admin/login"
              data-testid="footer-admin-link"
              className="text-[#5C6661]/70 hover:text-[#739072] transition whitespace-nowrap"
            >
              Admin
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};
